import { useState, useCallback } from 'react';
import type { Person } from '../types/people';
import { getPersonAvatarUrl, getPersonInitials } from '../utils/peopleAvatar';

interface AvatarStackProps {
  people: Person[];
  personIds: string[];
  size?: number;
}

function SingleAvatar({
  person,
  size,
  failed,
  onLoadFailed,
}: {
  person: Person;
  size: number;
  failed: boolean;
  onLoadFailed: () => void;
}) {
  if (failed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-blue-dark)',
          color: 'white',
          fontSize: Math.max(10, size * 0.4),
          fontWeight: 600,
          border: '2px solid white',
          boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.08)',
        }}
      >
        {getPersonInitials(person)}
      </div>
    );
  }
  return (
    <img
      src={getPersonAvatarUrl(person)}
      alt=""
      style={{ width: size, height: size, objectFit: 'cover', display: 'block', borderRadius: '50%', border: '2px solid white', boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.08)' }}
      onError={onLoadFailed}
    />
  );
}

export default function AvatarStack({ people, personIds, size = 24 }: AvatarStackProps) {
  const [failedIds, setFailedIds] = useState<Set<string>>(() => new Set());
  const markFailed = useCallback((id: string) => {
    setFailedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const assigned = personIds
    .map((id) => people.find((p) => p.id === id))
    .filter((p): p is Person => Boolean(p));

  if (assigned.length === 0) {
    return <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>—</span>;
  }

  const overlap = Math.round(size * 0.4);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ display: 'flex', position: 'relative', paddingLeft: assigned.length > 1 ? overlap : 0 }}>
        {assigned.map((person, index) => (
          <div
            key={person.id}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              overflow: 'hidden',
              marginLeft: index === 0 ? 0 : -overlap,
              backgroundColor: '#e2e8f0',
              flexShrink: 0,
            }}
          >
            <SingleAvatar
              person={person}
              size={size}
              failed={failedIds.has(person.id)}
              onLoadFailed={() => markFailed(person.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

